import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Pencil, AlertTriangle } from 'lucide-react';
import { StatusChip, CATEGORY_META, fmtDate } from './issueMeta';

const FLAG_TEXT = {
  deadline_moved: (f) => `Deadline moved: ${fmtDate(f.from)} → ${fmtDate(f.to)} (both kept in history)`,
  contradiction: (f) => f.note,
  verify_recommended: (f) => f.note,
  merged: (f) => f.note,
};

function EditFields({ item, edits, onSave, onCancel, id }) {
  const [f, setF] = useState({ title: item.title, statement: item.statement, status: item.status, category: item.category, deadline: item.deadline || '', ...edits });
  return (
    <div className="space-y-2 mt-2" data-testid={`proposal-edit-${id}`}>
      <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid={`proposal-edit-title-${id}`} />
      <Textarea rows={2} value={f.statement} onChange={(e) => setF({ ...f, statement: e.target.value })} data-testid={`proposal-edit-statement-${id}`} />
      <div className="grid grid-cols-3 gap-2">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`proposal-edit-status-${id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="progress">Progress reported</SelectItem>
            <SelectItem value="resolved_reported">Resolved – reported</SelectItem>
          </SelectContent>
        </Select>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`proposal-edit-category-${id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CATEGORY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" className="h-8 text-xs" value={f.deadline || ''} onChange={(e) => setF({ ...f, deadline: e.target.value || null })} data-testid={`proposal-edit-deadline-${id}`} />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(f)} data-testid={`proposal-edit-save-${id}`}>Save</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function ProposalCard({ p, openIssues, onPatch }) {
  const [editing, setEditing] = useState(false);
  const item = p.item ? { ...p.item, ...(p.edits || {}) } : null;
  const rejected = p.decision === 'rejected';
  return (
    <div className={`rounded-md border p-3 ${rejected ? 'opacity-50' : ''}`} data-testid={`proposal-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{item?.title || p.issue_title}</p>
          {p.kind === 'update' && item?.title !== p.issue_title && <p className="text-[11px] text-muted-foreground">Continues: {p.issue_title}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {item && <StatusChip status={item.status} />}
          {p.kind === 'not_mentioned' && <StatusChip status="no_update" />}
          {item && <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" onClick={() => setEditing(!editing)} data-testid={`proposal-edit-btn-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          <Button size="icon" variant={rejected ? 'default' : 'ghost'} className="h-7 w-7" title={rejected ? 'Undo reject' : 'Reject'}
            onClick={() => onPatch(p.id, { decision: rejected ? 'pending' : 'rejected' })} data-testid={`proposal-reject-${p.id}`}>
            {rejected ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      {item && !editing && (
        <>
          <p className="text-xs mt-1">{item.statement}</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {CATEGORY_META[item.category]?.label}
            {(item.deadline || item.deadline_text) && ` · Target: ${fmtDate(item.deadline) || item.deadline_text}`}
            {item.section && ` · §${String(item.section).replace(/^§/, '')}`}
          </p>
          {item.quote && <blockquote className="text-[11px] italic text-muted-foreground border-l-2 pl-2 mt-1 line-clamp-3">“{item.quote}”</blockquote>}
        </>
      )}
      {editing && <EditFields item={p.item} edits={p.edits} id={p.id} onCancel={() => setEditing(false)} onSave={(e) => { onPatch(p.id, { edits: e }); setEditing(false); }} />}
      {p.flags?.map((f, i) => (
        <p key={i} className="flex gap-1.5 text-[11px] text-amber-800 dark:text-amber-300 mt-1" data-testid={`proposal-flag-${f.type}-${p.id}`}>
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{FLAG_TEXT[f.type]?.(f) || f.type}
        </p>
      ))}
      {p.kind === 'uncertain' && (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`proposal-decide-${p.id}`}>
          <Select value={p.as_new ? 'new' : (p.link_issue_id || '')} onValueChange={(v) => onPatch(p.id, v === 'new' ? { as_new: true } : { link_issue_id: v })}>
            <SelectTrigger className="h-8 text-xs w-72" data-testid={`proposal-decide-select-${p.id}`}><SelectValue placeholder={`Same as “${p.issue_title}”?`} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">It's a new issue</SelectItem>
              {openIssues.map((i) => <SelectItem key={i.id} value={i.id}>Same as: {i.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
